import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ICONS, TAB_ICONS, SectionLabel, MetricGrid, MetricCard, SourceBars, FunnelBars,
  DeltaText, ArrowUp, ArrowDown, Flag, Sparkles, PlayCircle, Info,
} from "@/components/ui-bits";
import {
  VisitorAreaChart, VelocityLineChart, ForecastChart, MissionBarChart,
  StagePieChart, RevenueTierChart, ContentTypeChart,
} from "@/components/charts";
import DecisionsPanel, { emptyForm } from "@/components/DecisionsPanel";
import BriefPanel from "@/components/BriefPanel";
import CertifyPanel from "@/components/CertifyPanel";
import KnowledgePanel from "@/components/KnowledgePanel";
import ArchivePanel from "@/components/ArchivePanel";
import BaselinePanel from "@/components/BaselinePanel";
import ReportingPanel from "@/components/ReportingPanel";
import IntegrationsPanel from "@/components/IntegrationsPanel";
import TimelinePanel from "@/components/TimelinePanel";
import { StatusBadge } from "@/components/StatusBadge";
import LaunchStatusCard from "@/components/LaunchStatusCard";
import { LogOut, GitBranch } from "lucide-react";

const TABS = [
  { id: "summary", label: "Summary", new: false },
  { id: "brief", label: "Brief", new: true },
  { id: "velocity", label: "Velocity", new: true },
  { id: "forecast", label: "Forecast", new: true },
  { id: "mission", label: "Mission", new: true },
  { id: "marketing", label: "Marketing", new: false },
  { id: "funnel", label: "Funnel", new: false },
  { id: "customers", label: "Customers", new: false },
  { id: "revenue", label: "Revenue", new: false },
  { id: "email", label: "Email", new: false },
  { id: "partners", label: "Partners", new: false },
  { id: "content", label: "Content", new: true },
  { id: "goals", label: "Goals", new: true },
  { id: "decisions", label: "Decisions", new: true },
  { id: "certify", label: "Certify", new: true },
  { id: "knowledge", label: "Knowledge", new: true },
  { id: "archive", label: "Archive", new: true },
  { id: "baselines", label: "Baselines", new: true },
  { id: "reporting", label: "Reporting", new: true },
  { id: "integrations", label: "Integrations", new: true },
  { id: "timeline", label: "Timeline", new: true },
  { id: "advisor", label: "Advisor", new: true },
];

const dirClass = (d) => (d === "up" ? "up" : d === "down" ? "down" : "flat");

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("summary");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [promoteDraft, setPromoteDraft] = useState(null);
  const [connectors, setConnectors] = useState({});

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch((e) => setError("Unable to load intelligence data."));
    api.get("/integrations/connectors").then(({ data }) =>
      setConnectors(Object.fromEntries(data.connectors.map((c) => [c.panel, c])))).catch(() => {});
  }, []);

  const handlePromote = (rec) => {
    const eii = data?.summary?.executive_index?.score;
    setPromoteDraft({
      ...emptyForm(),
      title: rec.rec.length > 80 ? rec.rec.slice(0, 80) + "…" : rec.rec,
      recommendation_summary: rec.rec,
      opportunity: rec.obs,
      rationale: rec.supporting_metrics ? `Supporting metrics: ${rec.supporting_metrics}` : "",
      business_area: rec.business_area || "General",
      status: "Advisor Recommendation",
      source: "Executive Advisor",
      recommendation_id: rec.id || "",
      confidence: rec.confidence || "",
      priority: rec.priority || "",
      recommended_timeline: rec.recommended_timeline || "",
      related_eii: eii || "",
      related_kpis: (rec.related_kpis || []).join(", "),
      owner: rec.recommended_owner || "Angella",
    });
    setTab("decisions");
  };

  const today = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  if (error) return <div className="meos-loading" data-testid="dashboard-error">{error}</div>;
  if (!data) return <div className="meos-loading" data-testid="dashboard-loading">Loading executive intelligence…</div>;

  return (
    <div className="meos-page">
      <div className="meos-shell" id="meos2">
        <div className="meos-header">
          <div className="meos-logo">Marrsing Executive Operating System™ <span>Strategic Intelligence Platform</span></div>
          <div className="meos-header-right">
            <div className="meos-version">MEOS v2.0</div>
            <button className="meos-logout" onClick={logout} data-testid="logout-button"><LogOut size={13} /> Sign out</button>
          </div>
        </div>
        <div className="meos-date">{today} · Welcome back, {user?.name || "Angella"}</div>

        <div className="nav-tabs" data-testid="nav-tabs">
          {TABS.map((t) => {
            const Icon = TAB_ICONS[t.id];
            return (
              <button
                key={t.id}
                className={`tab-btn ${t.new ? "new-tab" : ""} ${tab === t.id ? "active" : ""}`}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
              >
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="panel" data-testid={`panel-${tab}`}>
          {connectors[tab] && (
            <div data-testid={`panel-status-${tab}`} style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
              <StatusBadge status={connectors[tab].status} lastSync={connectors[tab].last_sync} />
            </div>
          )}
          {connectors[tab] && connectors[tab].status === "awaiting" && (
            <div className="card" data-testid={`awaiting-banner-${tab}`} style={{ borderColor: "#BA7517", background: "#FBF3E4", marginBottom: 12 }}>
              <strong style={{ color: "#BA7517" }}>Awaiting Live Integration</strong>
              <div style={{ fontSize: 13, marginTop: 4, opacity: 0.8 }}>
                This module will automatically begin displaying live operational data once its production connector ({connectors[tab].connector}) is enabled. Figures shown are pre-launch reference values.
              </div>
            </div>
          )}
          {tab === "summary" && <><LaunchStatusCard /><Summary d={data.summary} /></>}
          {tab === "brief" && <BriefPanel />}
          {tab === "velocity" && <Velocity d={data.velocity} />}
          {tab === "forecast" && <Forecast d={data.forecast} />}
          {tab === "mission" && <Mission d={data.mission} />}
          {tab === "marketing" && <Marketing d={data.marketing} />}
          {tab === "funnel" && <Funnel d={data.funnel} />}
          {tab === "customers" && <Customers d={data.customers} />}
          {tab === "revenue" && <Revenue d={data.revenue} />}
          {tab === "email" && <Email d={data.email} />}
          {tab === "partners" && <Partners d={data.partners} />}
          {tab === "content" && <Content d={data.content} />}
          {tab === "goals" && <Goals d={data.goals} />}
          {tab === "decisions" && <DecisionsPanel initialDraft={promoteDraft} onDraftConsumed={() => setPromoteDraft(null)} />}
          {tab === "certify" && <CertifyPanel />}
          {tab === "knowledge" && <KnowledgePanel />}
          {tab === "archive" && <ArchivePanel />}
          {tab === "baselines" && <BaselinePanel />}
          {tab === "reporting" && <ReportingPanel />}
          {tab === "integrations" && <IntegrationsPanel />}
          {tab === "timeline" && <TimelinePanel />}
          {tab === "advisor" && <Advisor d={data.advisor} onPromote={handlePromote} />}
        </div>
      </div>
    </div>
  );
}

/* ---------------- SUMMARY ---------------- */
function Summary({ d }) {
  const h = d.health || {};
  return (
    <>
      <div className="north-star">
        <div className="ns-title">Executive North Star Scorecard</div>
        <div className="ns-grid">
          {d.north_star.map((c, i) => (
            <div className="ns-card" key={i}>
              <div className="ns-label">{c.label}</div>
              <div className="ns-value">{c.value}</div>
              <div className="ns-sub">{c.sub}</div>
            </div>
          ))}
        </div>
        <div className="health-ring">
          <div>
            <div className="ring-label">{h.label || "Executive Intelligence Index"}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
              <div className="ring-score" data-testid="health-score">{h.score}</div>
              <div className="ring-status rs-green">{h.status}</div>
            </div>
          </div>
          <div style={{ flex: 1, paddingLeft: 16, borderLeft: "1px solid rgba(255,255,255,.15)", minWidth: 220 }}>
            <div style={{ fontSize: 10, opacity: .6, marginBottom: 6 }}>Weighted KPI categories</div>
            <div style={{ fontSize: 11, opacity: .82, lineHeight: 1.9 }}>
              {(h.components || []).map((c, i) => (
                <span key={i}>{c.name} {c.score}<span style={{ opacity: .55 }}> ({Math.round(c.weight * 100)}%)</span>{i < h.components.length - 1 ? " · " : ""}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <SectionLabel>Today's KPIs</SectionLabel>
      <MetricGrid items={d.kpis} />

      <SectionLabel>Funnel health</SectionLabel>
      <div className="card" style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
        {d.funnel_health.map((f, i) => (
          <div key={i}>
            <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginBottom: 5 }}>{f.label}</div>
            <div className={`health-badge health-${f.level}`}><div className={`status-dot dot-${f.level}`} /> {f.status}</div>
          </div>
        ))}
      </div>

      <SectionLabel>7-day visitor trend</SectionLabel>
      <div className="card"><div className="chart-wrap"><VisitorAreaChart trend={d.visitor_trend} /></div></div>
    </>
  );
}

/* ---------------- VELOCITY ---------------- */
function Velocity({ d }) {
  return (
    <>
      <SectionLabel>Growth velocity — acceleration indicators</SectionLabel>
      <div className="card">
        <div className="velocity-header">
          <span>Metric</span><span>7 days</span><span>30 days</span><span>90 days</span><span>Status</span>
        </div>
        {d.rows.map((r, i) => {
          const Icon = ICONS[r.icon];
          return (
            <div className="velocity-row" key={i}>
              <span className="velocity-name">{Icon && <Icon size={13} />}{r.name}</span>
              <span className="velocity-val">{r.value}</span>
              <span className={`vel-period ${dirClass(r.dirs[0])}`}>{r.d7}</span>
              <span className={`vel-period ${dirClass(r.dirs[1])}`}>{r.d30}</span>
              <span className={`vel-period ${dirClass(r.dirs[2])}`}>{r.d90}</span>
              <span className={`velocity-badge vbadge-${r.badge}`}>{r.status}</span>
            </div>
          );
        })}
      </div>
      <SectionLabel>Velocity trend — 7 days</SectionLabel>
      <div className="card"><div className="chart-wrap"><VelocityLineChart trend={d.trend} /></div></div>
    </>
  );
}

/* ---------------- FORECAST ---------------- */
function Forecast({ d }) {
  return (
    <>
      <SectionLabel>Revenue projections — based on current trajectory</SectionLabel>
      <div className="three-col" style={{ marginBottom: 12 }}>
        {d.projections.map((p, i) => (
          <div className="card" key={i} style={i === 0 ? { borderColor: "#1F75A8" } : undefined}>
            <div className="card-title">{p.label}</div>
            <div style={{ fontSize: 26, fontWeight: 600, color: "#1F75A8", fontFamily: "'Cormorant Garamond', serif" }}>{p.value}</div>
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>Range: {p.range}</div>
            <div className={`tag ${p.level === "high" ? "tag-win" : "tag-opp"}`} style={{ marginLeft: 0, marginTop: 8, display: "inline-block" }}>{p.confidence}</div>
          </div>
        ))}
      </div>

      <SectionLabel>Forecast — 12-month revenue projection</SectionLabel>
      <div className="card"><div className="chart-wrap"><ForecastChart chart={d.chart} /></div></div>

      <SectionLabel>Milestone timelines — projected at current growth rate</SectionLabel>
      <div className="card">
        {d.milestones.map((m, i) => (
          <div className="milestone-row" key={i}>
            <Flag size={15} className="milestone-icon" />
            <span className="milestone-name">{m.name}</span>
            <span className="milestone-date">{m.eta}</span>
            <span className={`milestone-conf conf-${m.level}`}>{m.confidence}</span>
          </div>
        ))}
      </div>
      <div className="info-note"><Info size={13} /> All projections are based on current 30-day growth trajectory. Confidence increases as historical data accumulates. Ranges widen with time horizon.</div>
    </>
  );
}

/* ---------------- MISSION ---------------- */
function Mission({ d }) {
  return (
    <>
      <SectionLabel>Relationship impact — this is the mission</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 }}>
        {d.cards.map((c, i) => {
          const Icon = ICONS[c.icon];
          return (
            <div className="mission-card" key={i}>
              <div className="mission-num">{c.num}</div>
              <div className="mission-label">{Icon && <Icon size={13} />} {c.label}</div>
            </div>
          );
        })}
      </div>
      <SectionLabel>Relationship stage distribution — who we serve</SectionLabel>
      <div className="card"><SourceBars rows={d.stages} max={40} /></div>
      <SectionLabel>Mission momentum — couples served over time</SectionLabel>
      <div className="card"><div className="chart-wrap"><MissionBarChart momentum={d.momentum} /></div></div>
    </>
  );
}

/* ---------------- MARKETING ---------------- */
function Marketing({ d }) {
  return (
    <>
      <SectionLabel>Traffic by source</SectionLabel>
      <div className="card"><SourceBars rows={d.sources} /></div>
      <MetricGrid items={d.metrics} />
    </>
  );
}

/* ---------------- FUNNEL ---------------- */
function Funnel({ d }) {
  const mid = Math.ceil(d.heatmap.length / 2);
  const left = d.heatmap.slice(0, mid);
  const right = d.heatmap.slice(mid);
  return (
    <>
      <SectionLabel>Clarity funnel — conversion</SectionLabel>
      <div className="card"><FunnelBars stages={d.stages} /></div>
      <SectionLabel>Journey heatmap — engagement intensity</SectionLabel>
      <div className="two-col">
        <div>{left.map((c, i) => <div className={`heatmap-cell ${c.cls}`} key={i}><div className="hm-label">{c.label}</div><div className="hm-val">{c.val}</div></div>)}</div>
        <div>{right.map((c, i) => <div className={`heatmap-cell ${c.cls}`} key={i}><div className="hm-label">{c.label}</div><div className="hm-val">{c.val}</div></div>)}</div>
      </div>
    </>
  );
}

/* ---------------- CUSTOMERS ---------------- */
function Customers({ d }) {
  return (
    <>
      <SectionLabel>Relationship stage</SectionLabel>
      <div className="card"><div className="chart-wrap"><StagePieChart stages={d.stage_distribution} /></div></div>
      <SectionLabel>Demographics</SectionLabel>
      <div className="two-col">
        <div className="card"><div className="card-title">Age bands</div><SourceBars rows={d.age_bands} /></div>
        <div className="card">
          <div className="card-title">Device</div>
          <SourceBars rows={d.device} />
          <div className="divider" />
          <SourceBars rows={d.new_returning} />
        </div>
      </div>
    </>
  );
}

/* ---------------- REVENUE ---------------- */
function Revenue({ d }) {
  const live = d.source === "stripe_live";
  return (
    <>
      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div className="section-label" style={{ margin: 0 }}>Revenue overview</div>
        <div className="spacer" />
        <span className={`source-badge ${live ? "src-live" : "src-seed"}`} data-testid="revenue-source">
          {live ? "● Live · Stripe" : "○ Seed data — add Stripe key to go live"}
        </span>
      </div>
      <MetricGrid items={d.metrics} />
      <SectionLabel>Revenue by tier</SectionLabel>
      <div className="card"><div className="chart-wrap"><RevenueTierChart tiers={d.by_tier} /></div></div>
      {live && d.recent?.length > 0 && (
        <>
          <SectionLabel>Live payment activity</SectionLabel>
          <div className="card" data-testid="live-payments">
            <div className="pay-row pay-header"><span>Customer</span><span>Product</span><span>Amount</span><span>Status</span><span>Time</span></div>
            {d.recent.map((p, i) => (
              <div className="pay-row" key={i}>
                <span>{p.email}</span><span>{p.product}</span><span style={{ fontWeight: 500 }}>{p.amount}</span>
                <span className={`status-pill ${p.status === "succeeded" ? "st-Completed" : "st-Cancelled"}`}>{p.status}</span>
                <span className="decision-meta">{(p.created || "").slice(0, 19).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* ---------------- EMAIL ---------------- */
function Email({ d }) {
  const seq = d.sequence.map((s) => ({ label: s.label, value: `${s.value}%`, pct: null, width: s.value, color: "#1F75A8", best: s.best }));
  return (
    <>
      <MetricGrid items={d.metrics} />
      <SectionLabel>Sequence open rates</SectionLabel>
      <div className="card">
        {seq.map((s, i) => (
          <div className="funnel-bar" key={i}>
            <div className="funnel-bar-label">
              <span>{s.label}{s.best && <span className="tag tag-win">Best</span>}</span>
              <span>{s.value}</span>
            </div>
            <div className="funnel-bar-track"><div className="funnel-bar-fill" style={{ width: `${s.width}%` }} /></div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ---------------- PARTNERS ---------------- */
function Partners({ d }) {
  return (
    <>
      <MetricGrid items={d.metrics} />
      <SectionLabel>Partner performance</SectionLabel>
      <div className="card">
        <div className="ptable-header">
          <span>Partner</span><span>Leads</span><span>Purchases</span><span>Conv%</span><span>Revenue</span><span>ROI</span>
        </div>
        {d.table.map((r, i) => (
          <div className="ptable-row" key={i}>
            <span style={{ fontWeight: 500 }}>{r.name}</span>
            <span>{r.leads}</span>
            <span>{r.purchases}</span>
            <span style={{ color: "#1F75A8", fontWeight: 500 }}>{r.conv}</span>
            <span>{r.revenue}</span>
            <span>{r.best ? <span className="tag tag-win" style={{ marginLeft: 0 }}>Best</span> : r.roi}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/* ---------------- CONTENT ---------------- */
function Content({ d }) {
  return (
    <>
      <SectionLabel>Top performing content this week</SectionLabel>
      <div className="card" style={{ borderColor: "#1F75A8" }}>
        <span className="tag tag-win" style={{ marginLeft: 0 }}>Reel of the week</span>
        <div style={{ fontSize: 14, fontWeight: 500, marginTop: 8, marginBottom: 4 }}>{d.reel_of_week.title}</div>
        <div className="metric-grid" style={{ marginTop: 10, marginBottom: 0 }}>
          {d.reel_of_week.metrics.map((m, i) => <MetricCard key={i} m={m} />)}
        </div>
      </div>
      <SectionLabel>Content performance by type</SectionLabel>
      <div className="card"><div className="chart-wrap"><ContentTypeChart byType={d.by_type} /></div></div>
      <SectionLabel>Content intelligence summary</SectionLabel>
      <div className="three-col">
        {d.summary.map((m, i) => (
          <div className="metric-card" key={i}>
            <div className="mc-label">{m.label}</div>
            <div className="mc-value" style={{ fontSize: 13 }}>{m.value}</div>
            <DeltaText dir="up">{m.delta}</DeltaText>
          </div>
        ))}
      </div>
    </>
  );
}

/* ---------------- GOALS ---------------- */
function Goals({ d }) {
  return (
    <>
      <SectionLabel>Strategic goals — progress &amp; projected completion</SectionLabel>
      <div className="card">
        {d.goals.map((g, i) => (
          <div className="progress-row" key={i}>
            <div className="progress-label">
              <span>{g.label}</span>
              <span style={{ fontWeight: 500, color: "#1F75A8", whiteSpace: "nowrap" }}>{g.value}</span>
            </div>
            <div className="progress-track"><div className={`progress-fill ${g.color}`} style={{ width: `${g.pct}%` }} /></div>
            <div className={`progress-note note-${g.note_level}`}>{g.note}</div>
          </div>
        ))}
      </div>
      <SectionLabel>Goal status summary</SectionLabel>
      <MetricGrid items={d.status_summary} />
    </>
  );
}

/* ---------------- ADVISOR ---------------- */
function Advisor({ d, onPromote }) {
  const b = d.brief;
  return (
    <>
      <div className="advisor-brief" data-testid="advisor-brief">
        <div className="brief-greeting"><Sparkles size={18} color="#1F75A8" /> {b.greeting}</div>
        <div className="brief-text">{b.happened}</div>
        {b.sections.map((s, i) => (
          <div className="brief-section" key={i}>
            <div className="brief-section-label">{s.label}</div>
            <div className="brief-text">{s.text}</div>
          </div>
        ))}
      </div>

      {d.references?.length > 0 && (
        <>
          <SectionLabel>Institutional memory — aligned with past decisions</SectionLabel>
          <div className="card" data-testid="advisor-references">
            {d.references.map((r, i) => (
              <div className="ref-item" key={i}>{r}</div>
            ))}
          </div>
        </>
      )}

      <SectionLabel>Strategic decision intelligence — promote any recommendation</SectionLabel>
      <div className="card" data-testid="advisor-recommendations">
        {d.decisions.map((r, i) => (
          <div className="decision-row" key={i} style={{ gridTemplateColumns: ".24fr 1fr 1fr auto", alignItems: "start" }}>
            <span className="decision-num">{r.n}</span>
            <span className="decision-obs">{r.obs}</span>
            <span className="decision-rec">
              {r.rec}
              <div style={{ marginTop: 5, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                {r.priority && <span className={`badge-prio prio-${r.priority}`}>{r.priority}</span>}
                {r.confidence && <span className="decision-meta">Confidence {r.confidence}%</span>}
                {r.recommended_timeline && <span className="decision-meta">· {r.recommended_timeline}</span>}
              </div>
            </span>
            <button className="promote-btn" onClick={() => onPromote(r)} data-testid={`promote-btn-${r.n}`}>
              <GitBranch size={12} /> Promote
            </button>
          </div>
        ))}
      </div>

      <SectionLabel>Today's recommended actions</SectionLabel>
      <div className="card">
        {d.actions.map((a, i) => (
          <div className="action-row" key={i}>
            <PlayCircle size={16} className="action-icon" />
            <div>
              <div className="action-title">{a.title}</div>
              <div className="action-desc">{a.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
